import { Stack, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-pro";
import { DataGridPro } from "@mui/x-data-grid-pro";
import type { FC } from "react";
import { useMemo } from "react";
import { CsvExportButton } from "../../../../../modules/generic/components/csv-export-button";
import type { GroupedMomentAnalyticsData } from "../../../../../modules/stats/server/analytics-service";

export type DeliveryStat = Extract<
  GroupedMomentAnalyticsData["stats"][number],
  { stat_type: "delivery" }
>["data"];

export const MissingByPerson: FC<{
  missing: DeliveryStat[];
  isWhatsApp?: boolean;
  linkedPageInteractions?: boolean;
}> = ({ missing, isWhatsApp = false, linkedPageInteractions = false }) => {
  const rows = useMemo(() => {
    return missing.map((item, id) => {
      return {
        id,
        first_name: item.recipient_data?.first_name ?? "-",
        last_name: item.recipient_data?.last_name ?? "-",
        work_email: item.recipient_data?.email ?? "-",
      };
    });
  }, [missing]);

  const columns = useMemo(() => {
    const columnData: GridColDef<(typeof rows)[0]>[] = [
      {
        field: "first_name",
        headerName: isWhatsApp || linkedPageInteractions ? "First name" : "First Name",
        flex: 1,
      },
      {
        field: "last_name",
        headerName: isWhatsApp || linkedPageInteractions ? "Last name" : "Last Name",
        flex: 1,
      },
      {
        field: "work_email",
        headerName: linkedPageInteractions ? "Work email" : "Email",
        flex: 0.5,
      },
    ];
    return columnData;
  }, [isWhatsApp, linkedPageInteractions]);

  return (
    <Stack spacing={1} direction="column">
      <Typography variant="body2">
        {linkedPageInteractions ? (
          <>
            <strong>{`${missing.length} ${missing.length === 1 ? "person" : "people"}`}</strong> who
            received this message {missing.length === 1 ? "has" : "have"} not interacted with links
            on the page.
          </>
        ) : isWhatsApp ? (
          <>
            <strong>{`${missing.length} ${missing.length === 1 ? "person" : "people"}`}</strong> who
            received this message {missing.length === 1 ? "has" : "have"} not visited the page.
          </>
        ) : (
          <>
            It looks like{" "}
            <strong>{`${missing.length} ${missing.length === 1 ? "person" : "people"}`}</strong> who
            received this Communication {missing.length === 1 ? "has" : "have"} <strong>not</strong>{" "}
            interacted with it yet, but we don’t always get accurate figures for this.
          </>
        )}
      </Typography>
      <DataGridPro
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        slots={{ toolbar: CsvExportButton }}
        showToolbar
      />
    </Stack>
  );
};
